import React, { Fragment, ChangeEvent } from "react";

import LineGraph from "../LineGraph";
import AuxMenu from "../AuxMenu";
import withTitle from "../withTitle";

import { DateTime } from "luxon";

import "../graph.css";
import "../aux-menu.css";
import ViewToolbar from "../ViewToolbar";
import ExpandList, { ExpandListEvent } from "../ExpandList";
import DelayInput from "../DelayInput";
import { V1, V2 } from "../../Api";
import { WellKnownGroups } from "../AuthContext";
import { formatName, COLORS2 } from "../../Utilities";

interface IMetricsAnalysisViewState {
    isLoading: boolean | string;
    isMenuOpen: boolean;

    presets: any[];
    selectedPreset: any;

    lineTypes: any[];
    lines: any[];

    dateStart: string;
    dateEnd: string;

    datasets: Array<{
        id: string;
        name: string;
        yUnit: number;
        types: IDatasetTypeList;
    }>;
    selectedDataset: string;
    selectedFilter: string;
    selectedFilterSex: string;
    selectedDiffStart: string | null;

    axisYMin: number;
    axisYMax: number;
    lastColor: number;
    yScaleMode: "auto" | "manual";

    baselineDate: string;
    dayBasis: string;

    filters: DatasetFilterList;
}

interface IDatasetTypeList {
    doctor?: boolean;
    np?: boolean;
    marketer?: boolean;
    location?: boolean;
    sex?: boolean;
}

type KeyMap<T> = {
    [K in keyof T]: any[];
};

type DatasetFilterList = KeyMap<IDatasetTypeList>;

class MetricsAnalysisView extends React.Component<{}, IMetricsAnalysisViewState> {
    constructor(props: Readonly<{}>) {
        super(props);

        const now = DateTime.local();
        const state: IMetricsAnalysisViewState = {
            isLoading: false,
            isMenuOpen: false,

            presets: [],
            selectedPreset: "",

            dateStart: now.minus({ days: 365 }).toISODate(),
            dateEnd: now.toISODate(),

            filters: {},

            datasets: [],
            selectedDataset: "",
            selectedFilter: "",
            selectedFilterSex: "",
            selectedDiffStart: null,

            axisYMin: 0,
            axisYMax: 100,
            lastColor: 0,
            yScaleMode: "auto",

            baselineDate: "",
            dayBasis: "365",

            lineTypes: [],
            lines: [],
        };

        this.state = state;

        this.toggleMenu = this.toggleMenu.bind(this);
        this.deleteLine = this.deleteLine.bind(this);
        this.handleElementChange = this.handleElementChange.bind(this);
        this.addGraphLine = this.addGraphLine.bind(this);
        this.lineListButtonRender = this.lineListButtonRender.bind(this);
        this.lineListRender = this.lineListRender.bind(this);
    }

    public async componentDidMount() {
        try {
            const [doctors, nps, marketers, locations, datasets, presets] = await Promise.all([
                V1.get<any[]>(`employees/summary?group=${WellKnownGroups.Surgeon}`),
                V1.get<any[]>(`employees/summary?group=${WellKnownGroups.Provider}`),
                V1.get<any[]>(`employees/summary?group=${WellKnownGroups.Marketer}`),
                V2.get<any[]>(`locations?type=office`),
                V2.get<any[]>("metrics/dataset"),
                V2.get<any[]>("metrics/dataset/presets?group=type"),
            ]);

            doctors.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)));
            nps.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)));
            marketers.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)));
            locations.sort((a, b) => (a.shortName || a.name).localeCompare(b.shortName || b.name));
            datasets.sort((a, b) => a.name.localeCompare(b.name));
            presets.sort((a, b) => a.name.localeCompare(b.name));

            this.setState({
                filters: {
                    doctor: doctors,
                    np: nps,
                    marketer: marketers,
                    location: locations
                },
                datasets: datasets,
                presets: presets
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Could not load datasets"
            });
        }
    }

    public render() {
        const dataset = this.state.datasets.find(a => a.id === this.state.selectedDataset) || { types: {} };

        return (
            <div className="viewer-root viewer-full full-height-max">
                <AuxMenu title="Graph Options" isOpen={this.state.isMenuOpen} onRequestClose={this.toggleMenu}>
                    <section>
                        <h3>General</h3>
                        <div className="aux-group">
                            <label htmlFor="aux-date-start">Start Date</label>
                            <DelayInput id="aux-date-start" type="date" timeout={500} name="dateStart" value={this.state.dateStart} onChangeDelayed={e => this.handleElementChange(e, this.queryDatasets)} />
                        </div>
                        <div className="aux-group">
                            <label htmlFor="aux-date-end">End Date</label>
                            <DelayInput id="aux-date-end" type="date" timeout={500} name="dateEnd" value={this.state.dateEnd} onChangeDelayed={e => this.handleElementChange(e, this.queryDatasets)} />
                        </div>
                        <div className="aux-group">
                            <label htmlFor="aux-y-scale">Y Scale</label>
                            <select id="aux-y-scale" name="yScaleMode" value={this.state.yScaleMode} onChange={e => this.handleElementChange(e, this.computeRange)}>
                                <option value="auto">Auto</option>
                                <option value="manual">Manual</option>
                            </select>
                        </div>
                        {
                            this.state.yScaleMode === "manual"
                                ? <div className="aux-group">
                                    <label htmlFor="aux-y-scale-min">Y Scale &mdash; Min &amp; Max</label>
                                    <div className="control-group">
                                        <DelayInput id="aux-y-scale-min" type="number" timeout={500} name="axisYMin" value={this.state.axisYMin.toString()} step={1} onChangeDelayed={this.handleElementChange} />
                                        <DelayInput id="aux-y-scale-max" type="number" timeout={500} name="axisYMax" value={this.state.axisYMax.toString()} step={1} onChangeDelayed={this.handleElementChange} />
                                    </div>
                                </div>
                                : null
                        }
                        <div className="aux-group">
                            <label htmlFor="aux-baseline-date">Baseline Date</label>
                            <DelayInput id="aux-baseline-date" type="date" timeout={500} name="baselineDate" value={this.state.baselineDate} onChangeDelayed={e => this.handleElementChange(e, this.recalculateLines)} />
                        </div>
                    </section>
                    <section>
                        <h3>Lines</h3>
                        <div className="aux-group">
                            <label htmlFor="aux-day-basis">Line Day Basis</label>
                            <select id="aux-day-basis" name="dayBasis" value={this.state.dayBasis} onChange={e => this.handleElementChange(e, this.computeRange)}>
                                <option value="365">365 day rolling</option>
                                <option value="28">28 day rolling</option>
                                <option value="7">7 day rolling</option>
                            </select>
                        </div>
                        <div className="aux-group">
                            <label>Add Line(s)</label>
                            {/*<label htmlFor="aux-dataset">Dataset</label>*/}
                            <select id="aux-dataset" name="selectedDataset" value={this.state.selectedDataset} onChange={e => this.handleElementChange(e, this.clearAdditionalFilters)}>
                                <option value="">Select dataset...</option>
                                {this.state.datasets.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                            </select>
                            {
                                this.state.selectedDataset
                                    ? <>
                                        <select id="aux-type" name="selectedFilter" value={this.state.selectedFilter} onChange={this.handleElementChange}>
                                            <option value="">No Filter</option>
                                            {this.getListForTypes(dataset.types)}
                                        </select>
                                        {(dataset.types as any).sex
                                            ? <select id="aux-sex" name="selectedFilterSex" value={this.state.selectedFilterSex} onChange={this.handleElementChange}>
                                                <option value="">Male & Female</option>
                                                <option value="m">Male</option>
                                                <option value="f">Female</option>
                                            </select>
                                            : null}
                                    </>
                                    : null
                            }
                            {
                                this.state.selectedDataset
                                    ? <button type="button" className="button smaller push-right" title="Add selected line(s)" onClick={this.addGraphLine}>Add</button>
                                    : null
                            }
                        </div>
                        <div className="aux-group">
                            <ExpandList items={this.state.lines} itemRender={this.lineListRender} itemButtonRender={this.lineListButtonRender} itemIdField="id" disableAdd={true} onDelete={this.deleteLine} />
                        </div>
                    </section>
                </AuxMenu>
                <ViewToolbar>
                    <button type="button" className="icon-sliders" title="Graph Options..." onClick={this.toggleMenu} />
                </ViewToolbar>
                <div className="filter-control-box">
                    <select className="fill-space" name="selectedPreset" value={this.state.selectedPreset} onChange={e => this.handleElementChange(e, this.handlePresetChange)}>
                        <option value="">Custom</option>
                        {this.state.presets.map(a => <optgroup key={a.type} label={this.typeToText(a.type)}>
                            {a.items.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </optgroup>)}
                    </select>
                </div>
                <LineGraph
                    xAxis={{
                        min: 0, max: Math.min(Math.round(DateTime.fromISO(this.state.dateEnd).diff(DateTime.fromISO(this.state.dateStart), "days").days), 365 * 30),
                        title: "Date",
                        tickInterval: (x, c) => this.state.dateStart ? this.dateToX(DateTime.fromISO(this.state.dateStart).plus({ days: x }), c) : 100,
                        labelMinDistancePx: 60,
                        // gridLineMinDistancePx: 64,
                        converter: (x) => this.state.dateStart ? DateTime.fromISO(this.state.dateStart).plus({ days: x }).toFormat("MMM '\u2019'yy") : ""
                    }}
                    yAxis={{
                        min: this.state.axisYMin, max: this.state.axisYMax,
                        title: "Value",
                        tickInterval: 5,
                        highlightValue: this.state.baselineDate ? 0 : undefined
                    }}
                    lines={this.state.lines}>
                    {typeof this.state.isLoading === "string"
                        ? <div className="spinner-overlay error-text-big">{this.state.isLoading}</div>
                        : null}
                </LineGraph>
            </div>
        );
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>, afterCb?: () => void) {
        let value: string | number = e.currentTarget.value;
        const name = e.currentTarget.name;

        if ((name === "dateStart" || name === "dateEnd") && !value) {
            return;
        }

        if (name === "axisYMin" || name === "axisYMax") {
            value = parseInt(value);
        }

        if (name !== "selectedPreset") {
            this.setState({
                selectedPreset: "",
                [name]: value
            } as any, afterCb);
        }
        else {
            this.setState({
                [name]: value
            } as any, afterCb);
        }
    }

    private dateToX(date: DateTime, includeCurrent: boolean = false) {
        if (includeCurrent && date.startOf("month").equals(date)) {
            return 0;
        }
        else {
            return date.plus({ month: 1 }).startOf("month").diff(date, "days").days;
        }
    }

    private toggleMenu() {
        const isOpen = this.state.isMenuOpen;

        const changes: Partial<IMetricsAnalysisViewState> = {
            isMenuOpen: !isOpen
        };

        if (isOpen) {
            changes.selectedDataset = "";
            changes.selectedFilter = "";
        }

        this.setState(changes as any);
    }

    private lineListRender(line: any) {
        return <Fragment>
            <div style={{ background: line.color }} className="graph-line-square" />
            {line.isEditingLabel
                ? <textarea defaultValue={line.label} autoFocus={true} onBlur={e => this.setLineLabel(line, e.currentTarget.value)} />
                : <div onDoubleClick={() => this.toggleLineLabelEdit(line)}>{(line.label as string).split("\n").map(a => <div key={a}>{a}</div>)}</div>
            }
        </Fragment>;
    }

    private lineListButtonRender(line: any) {
        return <button type="button" className="button btn-light" title="Compare line..." onClick={() => this.selectLineComparison(line)}>
            {line.isSelected ? <i className="icon-dot-circle-o" /> : <i>&bull;</i>}
        </button>;
    }

    private toggleLineLabelEdit(line: any) {
        line.isEditingLabel = true;

        this.setState(s => ({
            lines: s.lines
        }));
    }

    private setLineLabel(line: any, text: string) {
        line.isEditingLabel = false;
        line.label = text;

        this.setState(s => ({
            lines: s.lines
        }));
    }

    private deleteLine(event: ExpandListEvent<any>) {
        let { lines, lastColor } = this.state;

        lines.splice(event.index || 0, 1);
        if (lines.length === 0) {
            lastColor = 0;
        }

        lines.forEach(a => a.isSelected = false);

        this.setState({
            lines,
            lastColor,
            selectedDiffStart: null,
            selectedPreset: "",
        }, this.computeRange);
    }

    private addGraphLine() {
        const { lines, selectedDataset, datasets, lastColor } = this.state;
        const ds = datasets.find(a => a.id === selectedDataset)!;
        const [group, id] = this.state.selectedFilter.split("/");
        let person;
        if (group) {
            if ((person = ((this.state.filters as any)[group] as any[]).find(a => a.id === id))) {
                if (group === "location")
                    person = person.shortName || person.name;
                else
                    person = formatName(person.name);
            }
        }

        const filterSex = this.state.selectedFilterSex;
        const sexString = filterSex ? `\n${this.sexToText(filterSex)}` : "";

        lines.push({
            id: Date.now().toString(),
            dataset: selectedDataset,
            label: `${ds.name.replace(" \u2014 ", " - ")}${(person ? "\n" + person : "")}${sexString}\n(${+this.state.dayBasis || 365} day)`,
            color: COLORS2[lastColor],
            filter: this.state.selectedFilter,
            filterSex: filterSex,
            dayBasis: +this.state.dayBasis || 365,
            yUnit: ds.yUnit,
            points: []
        });

        this.setState({
            lines: lines,
            lastColor: (lastColor + 1) % COLORS2.length,
            selectedPreset: "",
        }, this.queryDatasets);
    }

    private async queryDatasets() {
        try {
            const query = {
                sets: [] as any[],
                diffSets: [] as any[]
            };

            if (this.state.lines.length === 0) {
                return;
            }

            for (const item of this.state.lines) {
                if (item.baseSets) {
                    query.diffSets.push({
                        id: item.id,
                        dataset: item.dataset,
                        startDate: this.state.dateStart,
                        endDate: this.state.dateEnd,
                        sets: item.baseSets.map((a: any) => {
                            const [group, id] = (a.filter as string || "").split("/");

                            return {
                                dataset: a.dataset,
                                dayBasis: a.dayBasis,
                                filterId: id,
                                filterGroup: group,
                                filterSex: item.filterSex,
                            };
                        })
                    });
                }
                else if (item.dataset) {
                    const [group, id] = (item.filter as string || "").split("/");
                    query.sets.push({
                        id: item.id,
                        dataset: item.dataset,
                        startDate: this.state.dateStart,
                        endDate: this.state.dateEnd,
                        filterGroup: group,
                        filterId: id,
                        filterSex: item.filterSex,
                        dayBasis: item.dayBasis || 365
                    });
                }
            }

            const results = await V2.post<any>("metrics/dataset/query", query);

            const lines = this.state.lines;
            for (const key of Object.keys(results)) {
                const existing = lines.find(a => a.id === key);
                if (existing) {
                    existing.points = results[key];
                }
            }

            this.computeBaseline(lines);

            this.setState({
                lines: lines,
                isLoading: false,
            }, this.computeRange);
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Could not load data"
            });
        }
    }

    private getListForTypes(types: IDatasetTypeList) {
        const items: React.ReactChild[] = [];

        if (types.doctor) {
            items.push(<optgroup label="Doctors" key="doctors">
                {this.state.filters.doctor && this.state.filters.doctor.map(a => <option key={`doctor/${a.id}`} value={`doctor/${a.id}`}>{formatName(a.name)}</option>)}
            </optgroup>);
        }
        if (types.np) {
            items.push(<optgroup label="NPs" key="nps">
                {this.state.filters.np && this.state.filters.np.map(a => <option key={`np/${a.id}`} value={`np/${a.id}`}>{formatName(a.name)}</option>)}
            </optgroup>);
        }
        if (types.marketer) {
            items.push(<optgroup label="Marketers" key="marketers">
                {this.state.filters.marketer && this.state.filters.marketer.map(a => <option key={`marketer/${a.id}`} value={`marketer/${a.id}`}>{formatName(a.name)}</option>)}
            </optgroup>);
        }
        if (types.location) {
            items.push(<optgroup label="Locations" key="locations">
                {this.state.filters.location && this.state.filters.location.map(a => <option key={`location/${a.id}`} value={`location/${a.id}`}>{a.shortName || a.name}</option>)}
            </optgroup>);
        }

        return items;
    }

    private computeRange() {
        if (this.state.yScaleMode !== "auto") {
            return;
        }

        let max = -Infinity;
        let min = Infinity;
        for (const line of this.state.lines) {
            for (const pt of line.points) {
                max = Math.max(max, pt.y);
                min = Math.min(min, pt.y);
            }
        }

        // If no lines, min/max will be +-Infinity, so assume 9..91 (+10% ~= 0..100)
        if (!isFinite(max)) {
            max = 91;
        }
        if (!isFinite(min)) {
            min = 9;
        }

        let dynamicRange = max - min;
        if (dynamicRange === 0) {
            max++;
            dynamicRange++;
        }
        dynamicRange = Math.max(dynamicRange, 20);

        max = Math.ceil(max + dynamicRange * 0.1);
        min = Math.floor(min - dynamicRange * 0.1);

        max = Math.ceil(max / 5) * 5;
        min = Math.floor(min / 5) * 5;

        this.setState({
            axisYMax: max,
            axisYMin: min
        });
    }

    private selectLineComparison(line: any) {
        let { lines, selectedDiffStart, lastColor, selectedPreset } = this.state;

        line.isSelected = !line.isSelected;
        let selectedId = line.id;
        if (line.id === selectedDiffStart) {
            // Un-selecting self
            selectedId = null;
        }

        const shouldQuery = line.isSelected && selectedDiffStart;
        if (shouldQuery) {
            // Another is already selected; create new line
            selectedId = null;
            selectedPreset = "";

            const lineA = lines.find(a => a.id === selectedDiffStart)!;
            const lineB = line;
            const baseSets: any[] = [];

            if (lineA.yUnit === lineB.yUnit) {

                // Combine data sources for existing line, including existing base sets (diffing a diff line)
                if (lineA.baseSets) {
                    Array.prototype.push.apply(baseSets, lineA.baseSets);
                }
                else {
                    baseSets.push({
                        dataset: lineA.dataset,
                        filter: lineA.filter,
                        dayBasis: lineA.dayBasis,
                    });
                }

                if (lineB.baseSets) {
                    Array.prototype.push.apply(baseSets, lineB.baseSets);
                }
                else {
                    baseSets.push({
                        dataset: lineB.dataset,
                        filter: lineB.filter,
                        dayBasis: lineB.dayBasis,
                    });
                }

                // Find longest match at the beginning of the line labels
                let lastBreak = 0;
                for (let matchLength = 0; matchLength < Math.min(lineA.label.length, lineB.label.length); matchLength++) {
                    if (lineA.label.charCodeAt(matchLength) !== lineB.label.charCodeAt(matchLength)) {
                        break;
                    }
                    switch (lineA.label[matchLength]) {
                        case " ":
                        case "-":
                        case "\n":
                        case "\r":
                            lastBreak = matchLength;
                            break;
                        default: break;
                    }
                }

                // Concat the matching piece and the the remaining different pieces
                const newLabel = `${lineA.label.substring(0, lastBreak).trim()}\n${lineA.label.substring(lastBreak).trim()} vs\n${lineB.label.substring(lastBreak).trim()}`;

                lines.push({
                    id: Date.now().toString(),
                    label: newLabel,
                    color: COLORS2[lastColor],
                    points: [],
                    baseSets,
                    yUnit: lineA.yUnit
                });
                lastColor = (lastColor + 1) % COLORS2.length

                lines.forEach(a => a.isSelected = false);
            }
        }

        this.setState({
            lines: lines,
            selectedDiffStart: selectedId,
            lastColor: lastColor,
            selectedPreset: selectedPreset
        }, () => shouldQuery && this.queryDatasets());
    }

    private computeBaseline(lines: any[]) {
        let x: number | undefined;
        if (this.state.baselineDate) {
            x = DateTime.fromISO(this.state.baselineDate).diff(DateTime.fromISO(this.state.dateStart), "days").days;
        }

        for (const line of lines) {
            // Find baseline value
            let basePoint;
            if (x !== undefined) {
                if (line.points[0].x > x) {
                    // Base point is before this line's data, use oldest point
                    basePoint = line.points[0];
                }
                else if (line.points[line.points.length - 1].x < x) {
                    // Base point is after this line's data, use newest point
                    basePoint = line.points[line.points.length - 1];
                }
                else {
                    basePoint = line.points.find((a: any) => a.x === x);
                }
            }

            if (line.baseline !== undefined && (!basePoint || basePoint.y !== line.baseline)) {
                // Restore back to full values
                if (line.yUnit === 2) {
                    // Percent
                    for (let i = 0; i < line.points.length; i++) {
                        line.points[i].y += line.baseline;
                    }
                }
                else {
                    for (let i = 0; i < line.points.length; i++) {
                        line.points[i].y = (line.points[i].y / 100 * line.baseline) + line.baseline;
                    }
                }
                delete line.baseline;
            }

            if (basePoint) {
                // New baseline
                line.baseline = basePoint.y || 1;
                if (line.yUnit === 2) {
                    // Percent
                    for (let i = 0; i < line.points.length; i++) {
                        line.points[i].y -= line.baseline;
                    }
                }
                else {
                    for (let i = 0; i < line.points.length; i++) {
                        line.points[i].y = (line.points[i].y - line.baseline) * 100 / line.baseline;
                    }
                }
            }

        }
    }

    private async recalculateLines() {
        const { lines } = this.state;

        this.computeBaseline(lines);

        this.setState({
            lines
        }, () => this.computeRange());
    }

    private async handlePresetChange() {
        const { presets, selectedPreset } = this.state;

        const presetContainer = presets
            .map((a: any) => a.items)
            .reduce((p: any[], c: any[]) => [...p, ...c])
            .find((a: any) => a.id === selectedPreset);
        if (!presetContainer) {
            return;
        }

        const preset = JSON.parse(presetContainer.data);

        const newData: Partial<IMetricsAnalysisViewState> = {
            yScaleMode: "auto",
            axisYMin: 0,
            axisYMax: 100,
            baselineDate: preset.baselineDate
        };

        switch (preset.dateEnd) {
            case "now": newData.dateEnd = DateTime.local().toISODate(); break;
            default: newData.dateEnd = preset.dateEnd; break;
        }

        switch (preset.dateStart) {
            case "ytd": newData.dateStart = DateTime.local().minus({ days: 365 }).toISODate(); break;
            default: newData.dateStart = preset.dateStart; break;
        }

        if (preset.yScale) {
            newData.yScaleMode = preset.yScale.mode;
            newData.axisYMin = preset.min === undefined ? 0 : preset.min;
            newData.axisYMax = preset.max === undefined ? 100 : preset.max;
        }

        if (preset.lines) {
            newData.lines = preset.lines.map((a: any) => ({
                id: a.id,
                dataset: a.dataset,
                label: a.label || "NO LABEL",
                color: COLORS2[a.color || 0],
                dayBasis: a.dayBasis,
                yUnit: a.yUnit || 1,
                filter: a.filter,
                points: [],
                baseSets: a.baseSets ? a.baseSets.map((b: any) => ({
                    dataset: b.dataset,
                    filter: b.filter,
                    dayBasis: b.dayBasis
                })) : undefined
            }));

            newData.lastColor = (preset.lines.map((a: any) => a.color).reduce((p: number, v: number) => Math.max(p, v)) + 1) % COLORS2.length;
        }

        this.setState(newData as any, () => {
            this.queryDatasets();
        });
    }

    private clearAdditionalFilters() {
        const dataset = this.state.datasets.find(a => a.id === this.state.selectedDataset);
        if (!dataset || !dataset.types.sex) {
            // Sex filter notsupported; clear
            this.setState({
                selectedFilterSex: ""
            });
        }
    }

    private typeToText(type: string) {
        switch (type) {
            case "365d":
            case "ytd": return "365 day";
            case "7d": return "7 day";
            case "other": return "Others";
            default: return type;
        }
    }

    private sexToText(sex: string) {
        switch (sex) {
            case "f":
            case "F":
                return "Female";
            case "m":
            case "M":
                return "Male";
        }
    }
}

export default withTitle("Analysis Graph", MetricsAnalysisView);